import type {
	ForwardedRef,
	HTMLAttributes,
	ImgHTMLAttributes,
	KeyboardEventHandler,
	MouseEventHandler
} from 'react'
import { forwardRef, useState } from 'react'
// @qc-ignore box composes sibling components within the same public module
import { Loading } from '../Loading'
import { runBoxClick, runBoxKeyboardActivation } from './click'
import style from './style.module.sass'
import type { BoxClickHandler } from './click'

type BoxProps = {
	image?: string
	imageLoading?: ImgHTMLAttributes<HTMLImageElement>['loading']
	imageFetchPriority?: 'high' | 'low' | 'auto'
	icon?: string
	shadowless?: boolean
	loading?: boolean
	onClick?: BoxClickHandler
} & Omit<HTMLAttributes<HTMLDivElement>, 'onClick'>

export const Box = forwardRef(
	(
		{
			image,
			imageLoading,
			imageFetchPriority,
			icon,
			shadowless,
			loading = false,
			onClick,
			onKeyDown,
			className,
			children,
			...rest
		}: BoxProps,
		ref: ForwardedRef<HTMLDivElement>
	) => {
		const [asyncLoading, setAsyncLoading] = useState(false)
		const isLoading = loading || asyncLoading

		const handleClick: MouseEventHandler<HTMLDivElement> = (event) => {
			runBoxClick({
				event,
				onClick,
				isLoading,
				setAsyncLoading
			})
		}
		const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
			runBoxKeyboardActivation({
				event,
				isClickable: Boolean(onClick),
				isLoading,
				onKeyDown
			})
		}

		return (
			<div
				{...rest}
				ref={ref}
				data-yakiniku="box"
				onClick={handleClick}
				onKeyDown={handleKeyDown}
				role={onClick && rest.role == null ? 'button' : rest.role}
				tabIndex={onClick && rest.tabIndex == null ? 0 : rest.tabIndex}
				aria-busy={isLoading || undefined}
				className={`relative overflow-hidden ${style.box} ${onClick ? style.clickable : ''} ${isLoading ? style.loading : ''} ${shadowless ? '' : style.shadowed} ${className || ''}`}
			>
				{image && (
					<img
						src={image}
						data-yakiniku="box-image"
						className={`boxbg ${style.bg}`}
						loading={imageLoading}
						fetchPriority={imageFetchPriority}
						decoding="async"
						alt=""
					/>
				)}
				{icon && (
					<img
						src={icon}
						data-yakiniku="box-icon"
						className={`boxiconbg ${style.iconbg}`}
						alt=""
					/>
				)}
				{isLoading && (
					<div
						data-yakiniku="box-loading"
						className={style.loadingOverlay}
					>
						<Loading />
					</div>
				)}
				{children}
			</div>
		)
	}
)
